import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BusinessStageType, BusinessTabType } from '../../data/types';

type BusinessTabsProps = {
    startTab: BusinessTabType
    businessStage: BusinessStageType;
    setBusinessStage: (stage: BusinessStageType) => void;
}

const BusinessTabs: React.FC<BusinessTabsProps> = ({ startTab, businessStage, setBusinessStage }) => {
    const navigate = useNavigate();
    const [showStageDropdown, setShowStageDropdown] = useState(false);

    const stageOptions: { value: BusinessStageType; label: string }[] = [
        { value: 'dream', label: 'Dream Stage' },
        { value: 'build', label: 'Build Stage' },
        { value: 'scale', label: 'Scale Stage' }
    ];

    const handleTabChange = (tab: 'business' | 'focus-group' | 'recommendations' | 'menu') => {
        if (businessStage === 'dream') {
            if (tab === 'focus-group') {
                navigate('/focus-group'); 
            } else if (tab === 'business') {
                navigate('/business');
            } 
        } else if (businessStage === 'build') {
            if (tab === 'recommendations') {
                navigate('/business?stage=build');
            } else if (tab === 'focus-group') {
                navigate('/focus-group?stage=build'); 
            }
        } else if (businessStage === 'scale') {
            if (tab === 'focus-group') {
                navigate('/focus-group?stage=scale'); 
            } else if (tab === 'menu') {
                navigate('/business?stage=scale');
            }
        }
    };

    return (
        <div className="border-b border-gray-200 sm:mt-[60px] mt-[185px]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Tabs */}
                    {
                        businessStage === 'dream' ? (
                            <nav className="flex space-x-8 -mb-px font-raleway">
                                <button
                                    className={`${
                                        startTab.dream === 'business'
                                            ? 'border-0 text-primaryDark-100 font-bold'
                                            : 'border-transparent text-gray-500  hover:text-primaryDark-300/50'
                                    } whitespace-nowrap py-4 px-1 text-sm transition-colors duration-200 text-[20px]`}
                                    onClick={() => handleTabChange('business')}
                                >
                                    My Business
                                </button>
                                <button
                                    className={`${
                                        startTab.dream === 'focus-group'
                                            ? 'border-0 text-primaryDark-100 font-bold'
                                            : 'border-transparent text-gray-500 hover:text-primaryDark-300/50'
                                    } whitespace-nowrap py-4 px-1 text-sm transition-colors duration-200 text-[20px]`}
                                    onClick={() => handleTabChange('focus-group')}
                                >
                                    Focus Group
                                </button>
                            </nav>
                        ) : (
                            businessStage === 'build' ? (
                                <nav className="flex space-x-8 -mb-px font-raleway">
                                    <button
                                        className={`${
                                            startTab.build === 'recommendations'
                                                ? 'border-0 text-primaryDark-100 font-bold'
                                                : 'border-transparent text-gray-500  hover:text-primaryDark-300/50'
                                        } whitespace-nowrap py-4 px-1 text-sm transition-colors duration-200 text-[20px]`}
                                        onClick={() => handleTabChange('recommendations')}
                                    >
                                        Recommendations
                                    </button>
                                    <button
                                        className={`${
                                            startTab.build === 'focus-group'
                                                    ? 'border-0 text-primaryDark-100 font-bold'
                                                    : 'border-transparent text-gray-500 hover:text-primaryDark-300/50'
                                            } whitespace-nowrap py-4 px-1 text-sm transition-colors duration-200 text-[20px]`}
                                            onClick={() => handleTabChange('focus-group')}
                                        >
                                            Focus Group
                                    </button>
                                </nav>
                            ) : (
                                businessStage === 'scale' ? (
                                    <nav className="flex space-x-8 -mb-px font-raleway">
                                        <button
                                            className={`${
                                                startTab.scale === 'menu'
                                                    ? 'border-0 text-primaryDark-100 font-bold'
                                                    : 'border-transparent text-gray-500  hover:text-primaryDark-300/50'
                                            } whitespace-nowrap py-4 px-1 text-sm transition-colors duration-200 text-[20px]`}
                                            onClick={() => handleTabChange('menu')}
                                        >
                                            Food Menu
                                        </button>
                                        <button
                                            className={`${
                                                startTab.scale === 'focus-group'
                                                        ? 'border-0 text-primaryDark-100 font-bold'
                                                        : 'border-transparent text-gray-500 hover:text-primaryDark-300/50'
                                                } whitespace-nowrap py-4 px-1 text-sm transition-colors duration-200 text-[20px]`}
                                                onClick={() => handleTabChange('focus-group')}
                                            >
                                                Focus Group
                                        </button>
                                    </nav>
                                ) : (
                                    <nav className="flex space-x-8 -mb-px font-raleway"></nav>
                                )
                            )
                        )
                    }

                    {/* Stage Dropdown */}
                    <div className="relative">
                        <button
                            className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primaryDark-400 transition-colors duration-200"
                            onClick={() => setShowStageDropdown(!showStageDropdown)}
                        >
                            <span className="font-open-sans">{stageOptions.find(option => option.value === businessStage)?.label}</span>
                            <svg
                                className={`ml-2 h-5 w-5 text-gray-400 transition-transform duration-200 ${
                                    showStageDropdown ? 'transform rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {showStageDropdown && (
                            <div 
                                className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                                onMouseLeave={() => setShowStageDropdown(false)}
                            >
                                <div className="py-1" role="menu">
                                    {stageOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-open-sans transition-colors duration-150 border-0"
                                            onClick={() => {
                                                setBusinessStage(option.value);
                                                setShowStageDropdown(false);
                                            }}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BusinessTabs; 